export const contract = [
    {
      title: "Contrato ERC20 explicado paso a paso: copia, pega y personaliza tu token",
      value: `
  A continuación tienes un contrato ERC20 listo para usarse en Polygon (o cualquier red EVM compatible), **totalmente comentado** y explicado.  
  Copia todo el contenido en tu editor de texto (como Sublime Text) o en [Remix IDE](https://remix.ethereum.org/) para personalizarlo, compilarlo y desplegarlo.
  
  **¿Qué hace este contrato?**  
  - Permite emitir un token fungible (ERC20) totalmente compatible con exchanges, wallets y plataformas DeFi.
  - Incluye funciones para transferir, aprobar, consultar balances y delegar transferencias.
  - Permite que el propietario transfiera la propiedad del contrato y recupere tokens enviados accidentalmente.
  
  ---
  
  \`\`\`solidity
  // SPDX-License-Identifier: MIT                 // Licencia abierta: cualquiera puede usar este código.
  pragma solidity ^0.8.0;                        // Especifica la versión del compilador Solidity.
  
  // =======================
  // Interfaz ERC20 estándar
  // =======================
  contract ERC20Interface {
      function totalSupply() public view virtual returns (uint);          // Devuelve el suministro total de tokens.
      function balanceOf(address tokenOwner) public view virtual returns (uint balance); // Saldo de una dirección.
      function allowance(address tokenOwner, address spender) public view virtual returns (uint remaining); // Permiso otorgado a otro usuario.
      function transfer(address to, uint tokens) public virtual returns (bool success); // Transfiere tokens.
      function approve(address spender, uint tokens) public virtual returns (bool success); // Aprueba a otro usuario para gastar tokens.
      function transferFrom(address from, address to, uint tokens) public virtual returns (bool success); // Transferencia delegada.
  
      event Transfer(address indexed from, address indexed to, uint tokens); // Evento: se transfiere un token.
      event Approval(address indexed tokenOwner, address indexed spender, uint tokens); // Evento: se aprueba gasto.
  }
  
  // ====================
  // Módulo de propiedad
  // ====================
  contract Owned {
      address public owner;        // Dirección del propietario.
      address public newOwner;     // Dirección del nuevo propietario (al transferir propiedad).
  
      event OwnershipTransferred(address indexed from, address indexed to); // Evento: transferencia de propiedad.
  
      constructor() {
          owner = msg.sender;      // Al desplegar el contrato, el creador es el propietario.
      }
  
      modifier onlyOwner {
          require(msg.sender == owner, "Not the owner");
          _;
      }
  
      function transferOwnership(address _newOwner) public onlyOwner {
          newOwner = _newOwner;    // Inicia la transferencia de propiedad.
      }
  
      function acceptOwnership() public {
          require(msg.sender == newOwner, "Not new owner");
          emit OwnershipTransferred(owner, newOwner);
          owner = newOwner;        // El nuevo propietario acepta.
          newOwner = address(0);   // Limpia el campo.
      }
  }
  
  // =============================================
  // Contrato principal ERC20: TootyrToken (ejemplo)
  // =============================================
  contract TootyrToken is ERC20Interface, Owned {
      string public symbol = "TRT";        // Abreviatura del token (personaliza aquí).
      string public name = "TootyrToken";  // Nombre del token (personaliza aquí).
      uint8 public decimals = 18;          // Decimales estándar ERC20.
      uint public _totalSupply = 100_000_000 * 10 ** uint(decimals); // Suministro total.
  
      mapping(address => uint) balances;                         // Registro de saldos.
      mapping(address => mapping(address => uint)) allowed;       // Permisos de gasto (allowance).
  
      // Constructor: recibe la dirección inicial y le asigna todos los tokens al desplegar.
      constructor(address initialHolder) {
          balances[initialHolder] = _totalSupply;
          emit Transfer(address(0), initialHolder, _totalSupply);
      }
  
      // Devuelve el suministro total menos los tokens quemados (en address(0)).
      function totalSupply() public view override returns (uint) {
          return _totalSupply - balances[address(0)];
      }
  
      // Consulta saldo de cualquier usuario.
      function balanceOf(address tokenOwner) public view override returns (uint balance) {
          return balances[tokenOwner];
      }
  
      // Transferencia simple: envía tokens a otro usuario.
      function transfer(address to, uint tokens) public override returns (bool success) {
          require(to != address(0), "Cannot transfer to zero address");
          require(balances[msg.sender] >= tokens, "Insufficient balance");
          balances[msg.sender] -= tokens;
          balances[to] += tokens;
          emit Transfer(msg.sender, to, tokens);
          return true;
      }
  
      // Aprueba a otro usuario para gastar tokens en tu nombre.
      function approve(address spender, uint tokens) public override returns (bool success) {
          allowed[msg.sender][spender] = tokens;
          emit Approval(msg.sender, spender, tokens);
          return true;
      }
  
      // Permite transferir tokens aprobados por allowance.
      function transferFrom(address from, address to, uint tokens) public override returns (bool success) {
          require(tokens <= balances[from], "Insufficient balance");
          require(tokens <= allowed[from][msg.sender], "Allowance exceeded");
          balances[from] -= tokens;
          allowed[from][msg.sender] -= tokens;
          balances[to] += tokens;
          emit Transfer(from, to, tokens);
          return true;
      }
  
      // Consulta la cantidad que un usuario puede gastar en nombre de otro.
      function allowance(address tokenOwner, address spender) public view override returns (uint remaining) {
          return allowed[tokenOwner][spender];
      }
  
      // Solo el owner puede recuperar tokens ERC20 enviados por error a este contrato.
      function transferAnyERC20Token(address tokenAddress, uint tokens) public onlyOwner returns (bool success) {
          return ERC20Interface(tokenAddress).transfer(owner, tokens);
      }
  }
  \`\`\`
  
  ---
  
  ### **¿Cómo usar este contrato?**
  1. **Copia el código completo arriba.**
  2. **Pega el contrato en Sublime Text, Remix IDE, o tu editor preferido.**
  ![Guarda el contrato en Sublime Text](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/12-1.png)
  3. **Personaliza el nombre y símbolo** en las líneas:
     \`string public symbol = "TRT";\`
     \`string public name = "TootyrToken";\`
  4. **Guarda el archivo como \`TootyrToken.sol\`** para su fácil identificación.
  5. **En las siguientes pasos vamos a compílarlo en Remix**, verificaremos que no haya errores y vamos a prepáralo para el despliegue.
  
  ---
  
  > **Consejo:** Lee cada comentario dentro del código para comprender la función de cada parte. Modifica solo lo necesario (nombre, símbolo, supply inicial) para adaptarlo a tu propio proyecto.
  
  [Ver contrato ERC20 en GitHub Gist](https://gist.githubusercontent.com/Tootyr/8c2afcc5fa222de03444c2b05a1bf0fd/raw/f75f6559a36a4451b4a77cc302a908f79d127d1e/TootyrToken)  
  
  `
    }
  ];
  
  export default contract;
  